// Takvimin saf model katmanı — React'e bağımlı değildir, tamamen birim testlidir.
// Blazor karşılığı: Octo.Web.Server/Components/Widgets/CalendarWidget.razor (DxScheduler) — DevExpress
// Scheduler yerine ay/hafta/gün/ajanda görünümleri CSS grid ile çizildiği için tüm tarih hesabı,
// hücre üretimi ve çakışma yerleşimi burada yapılır.
//
// Kurallar:
// - Hafta Pazartesi başlar (Blazor FirstDayOfWeek = 1).
// - Tüm gün anahtarları YEREL bileşenlerden üretilir (toISOString kullanılmaz; dilim kayması olmaz).
// - Tekrar (RRULE) genişletmesi sunucuda yapılır; buradaki "genişletme" yalnızca çok günlü
//   etkinliğin kapsadığı her güne dağıtılmasıdır.

import { parseDateValue } from '@/shared/lib/octo/dateValue';
import type {
  CalendarEvent,
  CalendarSource,
  CalendarViewKind,
  DateRange,
  MergedCalendarEvent,
} from './calendarTypes';
import { CALENDAR_SOURCE_TYPE } from './calendarTypes';

export const MINUTES_PER_DAY = 1440;
export const DEFAULT_SOURCE_COLOR = '#3b82f6';

const TR_MONTHS = [
  'Ocak',
  'Şubat',
  'Mart',
  'Nisan',
  'Mayıs',
  'Haziran',
  'Temmuz',
  'Ağustos',
  'Eylül',
  'Ekim',
  'Kasım',
  'Aralık',
];

/** Pazartesi başlangıçlı kısa gün adları. */
const TR_WEEKDAYS_SHORT = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
/** Pazartesi başlangıçlı uzun gün adları. */
const TR_WEEKDAYS_LONG = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

const pad = (n: number) => String(n).padStart(2, '0');

// ─────────────────────────────── Tarih yardımcıları ───────────────────────────────

/** Günün başlangıcı (yerel gece yarısı) — yaz saati geçişlerinde de güvenlidir. */
export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Günün sonu (ertesi günün gece yarısı, dışlayıcı üst sınır). */
export function endOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
}

export function addDays(d: Date, n: number): Date {
  return new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate() + n,
    d.getHours(),
    d.getMinutes(),
    d.getSeconds(),
  );
}

/** Ay ekler; hedef ayda gün yoksa ayın son gününe kırpar (31 Ocak + 1 ay = 28/29 Şubat). */
export function addMonths(d: Date, n: number): Date {
  const target = new Date(d.getFullYear(), d.getMonth() + n, 1);
  const lastDay = daysInMonth(target.getFullYear(), target.getMonth());
  target.setDate(Math.min(d.getDate(), lastDay));
  return target;
}

export function daysInMonth(year: number, monthIndex: number): number {
  return new Date(year, monthIndex + 1, 0).getDate();
}

/** Dilimden bağımsız gün anahtarı: "YYYY-MM-DD" (yerel bileşenlerden). */
export function dateKey(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** Haftanın kaçıncı günü (weekStartsOn'a göre 0..6). */
export function weekdayIndex(d: Date, weekStartsOn = 1): number {
  return (d.getDay() - weekStartsOn + 7) % 7;
}

/** Haftanın ilk günü — varsayılan Pazartesi. */
export function startOfWeek(d: Date, weekStartsOn = 1): Date {
  const s = startOfDay(d);
  return new Date(s.getFullYear(), s.getMonth(), s.getDate() - weekdayIndex(s, weekStartsOn));
}

/** Gün içi dakika (0..1440) — yerel saat. */
export function minutesOfDay(d: Date): number {
  return d.getHours() * 60 + d.getMinutes() + d.getSeconds() / 60;
}

function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}

// ─────────────────────────────── Ay / hafta / gün ızgaraları ───────────────────────────────

export interface MonthDay {
  date: Date;
  key: string;
  /** Görüntülenen aya mı ait (önceki/sonraki ay taşmaları false). */
  inMonth: boolean;
  isToday: boolean;
  isWeekend: boolean;
}

export interface MonthGrid {
  year: number;
  monthIndex: number;
  /** Her biri 7 günlük satırlar (4..6 satır). */
  weeks: MonthDay[][];
  /** Izgaranın kapsadığı yarı açık aralık. */
  range: DateRange;
}

/**
 * Ay ızgarası: ayın ilk gününün haftasından, son gününün haftasının sonuna kadar.
 * Satır sayısı sabitlenmez — Şubat 2021 gibi tam oturan aylarda 4 satır üretir.
 */
export function buildMonthGrid(
  anchor: Date,
  options: { weekStartsOn?: number; today?: Date } = {},
): MonthGrid {
  const weekStartsOn = options.weekStartsOn ?? 1;
  const today = startOfDay(options.today ?? new Date());
  const year = anchor.getFullYear();
  const monthIndex = anchor.getMonth();

  const first = new Date(year, monthIndex, 1);
  const last = new Date(year, monthIndex, daysInMonth(year, monthIndex));
  const gridStart = startOfWeek(first, weekStartsOn);
  const gridEndExclusive = addDays(startOfWeek(last, weekStartsOn), 7);
  const totalDays = Math.round((gridEndExclusive.getTime() - gridStart.getTime()) / 86_400_000);

  const weeks: MonthDay[][] = [];
  for (let i = 0; i < totalDays; i++) {
    const date = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i);
    const dow = date.getDay();
    const cell: MonthDay = {
      date,
      key: dateKey(date),
      inMonth: date.getMonth() === monthIndex && date.getFullYear() === year,
      isToday: isSameDay(date, today),
      isWeekend: dow === 0 || dow === 6,
    };
    if (i % 7 === 0) weeks.push([]);
    weeks[weeks.length - 1]!.push(cell);
  }

  return { year, monthIndex, weeks, range: { start: gridStart, end: gridEndExclusive } };
}

/** Haftanın 7 günü (Pazartesi başlangıçlı). */
export function buildWeekDays(anchor: Date, weekStartsOn = 1): Date[] {
  const start = startOfWeek(anchor, weekStartsOn);
  return Array.from(
    { length: 7 },
    (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i),
  );
}

export interface TimeSlot {
  /** Gün başından itibaren dakika. */
  minutes: number;
  hour: number;
  minute: number;
  /** "08:00" */
  label: string;
}

/** Hafta/gün görünümünün saat satırları. */
export function buildTimeSlots(startHour = 0, endHour = 24, stepMinutes = 60): TimeSlot[] {
  const from = clamp(Math.floor(startHour), 0, 24) * 60;
  const to = clamp(Math.ceil(endHour), 0, 24) * 60;
  const step = Math.max(5, stepMinutes);
  const slots: TimeSlot[] = [];
  for (let m = from; m < to; m += step) {
    slots.push({
      minutes: m,
      hour: Math.floor(m / 60),
      minute: m % 60,
      label: `${pad(Math.floor(m / 60))}:${pad(m % 60)}`,
    });
  }
  return slots;
}

/** Görünümün kapsadığı yarı açık tarih aralığı. */
export function visibleRange(
  anchor: Date,
  view: CalendarViewKind,
  options: { weekStartsOn?: number; agendaDays?: number } = {},
): DateRange {
  const weekStartsOn = options.weekStartsOn ?? 1;
  switch (view) {
    case 'month':
      return buildMonthGrid(anchor, { weekStartsOn }).range;
    case 'week': {
      const start = startOfWeek(anchor, weekStartsOn);
      return { start, end: addDays(start, 7) };
    }
    case 'day': {
      const start = startOfDay(anchor);
      return { start, end: addDays(start, 1) };
    }
    case 'agenda': {
      const start = startOfDay(anchor);
      return { start, end: addDays(start, options.agendaDays ?? 30) };
    }
  }
}

/** Görünüme göre ileri/geri gezinme (step: -1 | +1). */
export function shiftAnchor(
  anchor: Date,
  view: CalendarViewKind,
  step: number,
  options: { agendaDays?: number } = {},
): Date {
  switch (view) {
    case 'month':
      return addMonths(anchor, step);
    case 'week':
      return addDays(anchor, 7 * step);
    case 'day':
      return addDays(anchor, step);
    case 'agenda':
      return addDays(anchor, (options.agendaDays ?? 30) * step);
  }
}

// ─────────────────────────────── Etkinlik normalizasyonu ───────────────────────────────

/**
 * Birleşik uçtan gelen etkinlikleri görünümlerin kullandığı biçime çevirir.
 * `sources` verilirse yerel (Local) kaynağa ait etkinlikler düzenlenebilir işaretlenir.
 */
export function normalizeEvents(
  raw: readonly MergedCalendarEvent[] | null | undefined,
  sources?: readonly CalendarSource[],
): CalendarEvent[] {
  if (!raw?.length) return [];
  const byId = new Map<string, CalendarSource>();
  for (const s of sources ?? []) byId.set(s.calendarSourceId, s);

  const out: CalendarEvent[] = [];
  for (const e of raw) {
    const start = parseDateValue(e.start);
    if (!start) continue;
    let end = parseDateValue(e.end) ?? start;
    if (end.getTime() < start.getTime()) end = start;

    const source = byId.get(e.calendarSourceId);
    const isLocal = source ? source.sourceType === CALENDAR_SOURCE_TYPE.Local : !e.eventId.includes('_');
    const readOnly = e.isReadOnly || !isLocal || (source?.isReadOnly ?? false);

    out.push({
      id: e.eventId,
      sourceId: e.calendarSourceId,
      sourceName: source?.sourceName ?? e.sourceName ?? '',
      color: source?.color ?? e.sourceColor ?? DEFAULT_SOURCE_COLOR,
      title: e.subject?.trim() ? e.subject : '(Başlıksız)',
      description: e.description ?? '',
      location: e.location ?? '',
      start: e.allDay ? startOfDay(start) : start,
      end: e.allDay ? endOfDay(end) : end,
      allDay: e.allDay,
      readOnly,
      isLocal,
      labelId: e.labelId ?? 0,
      statusId: e.statusId ?? 0,
    });
  }
  return sortEvents(out);
}

/** Tüm gün önce, sonra başlangıç saatine göre. */
export function sortEvents(events: readonly CalendarEvent[]): CalendarEvent[] {
  return [...events].sort(
    (a, b) =>
      Number(b.allDay) - Number(a.allDay) ||
      a.start.getTime() - b.start.getTime() ||
      b.end.getTime() - a.end.getTime() ||
      a.id.localeCompare(b.id),
  );
}

/** Aralıkla kesişen etkinlikler (yarı açık aralık; sıfır süreli etkinlik başlangıcıyla kapsanır). */
export function eventsInRange(events: readonly CalendarEvent[], range: DateRange): CalendarEvent[] {
  const from = range.start.getTime();
  const to = range.end.getTime();
  return events.filter((e) => {
    const s = e.start.getTime();
    const en = Math.max(e.end.getTime(), s + 1);
    return s < to && en > from;
  });
}

/**
 * Etkinlikleri gün anahtarına dağıtır; çok günlü etkinlik kapsadığı HER günde görünür.
 * (Tekrar genişletmesi sunucudadır — burada yalnızca gün dağıtımı yapılır.)
 */
export function expandEventsByDay(
  events: readonly CalendarEvent[],
  days: readonly Date[],
): Map<string, CalendarEvent[]> {
  const map = new Map<string, CalendarEvent[]>();
  for (const day of days) map.set(dateKey(day), []);
  if (!map.size) return map;

  for (const e of events) {
    const s = e.start.getTime();
    const en = Math.max(e.end.getTime(), s + 1);
    for (const day of days) {
      const dayStart = startOfDay(day).getTime();
      const dayEnd = endOfDay(day).getTime();
      if (s < dayEnd && en > dayStart) map.get(dateKey(day))!.push(e);
    }
  }
  for (const [k, list] of map) map.set(k, sortEvents(list));
  return map;
}

/** Bir günün etkinliklerini tüm gün / saatli olarak ayırır. */
export function splitDayEvents(
  events: readonly CalendarEvent[],
  day: Date,
): { allDay: CalendarEvent[]; timed: CalendarEvent[] } {
  const dayStart = startOfDay(day).getTime();
  const dayEnd = endOfDay(day).getTime();
  const allDay: CalendarEvent[] = [];
  const timed: CalendarEvent[] = [];
  for (const e of events) {
    const s = e.start.getTime();
    const en = Math.max(e.end.getTime(), s + 1);
    if (s >= dayEnd || en <= dayStart) continue;
    // Tüm gün işaretli ya da günün tamamını kaplayan (çok günlü) etkinlikler üst şeritte gösterilir.
    if (e.allDay || (s <= dayStart && en >= dayEnd)) allDay.push(e);
    else timed.push(e);
  }
  return { allDay: sortEvents(allDay), timed: sortEvents(timed) };
}

// ─────────────────────────────── Gün içi çakışma yerleşimi ───────────────────────────────

export interface PositionedEvent {
  event: CalendarEvent;
  /** 0 tabanlı sütun indeksi. */
  column: number;
  /** Çakışma kümesindeki toplam sütun sayısı. */
  columnCount: number;
  /** Pencereye göre yüzde konum (0..100). */
  top: number;
  height: number;
  /** Gün başından itibaren dakika (pencereye kırpılmış). */
  startMinutes: number;
  endMinutes: number;
}

/**
 * Gün içindeki saatli etkinlikleri sütunlara yerleştirir (çakışanlar yan yana).
 * Çakışma kümesi: zincirleme kesişen etkinlikler; her küme kendi sütun sayısını kullanır.
 */
export function layoutDayEvents(
  events: readonly CalendarEvent[],
  day: Date,
  options: { startHour?: number; endHour?: number; minDurationMinutes?: number } = {},
): PositionedEvent[] {
  const windowStart = clamp(options.startHour ?? 0, 0, 24) * 60;
  const windowEnd = clamp(options.endHour ?? 24, 0, 24) * 60;
  const span = Math.max(1, windowEnd - windowStart);
  const minDur = options.minDurationMinutes ?? 15;
  const dayStart = startOfDay(day).getTime();

  const items = events
    .filter((e) => !e.allDay)
    .map((e) => {
      const from = clamp((e.start.getTime() - dayStart) / 60_000, 0, MINUTES_PER_DAY);
      const rawTo = (e.end.getTime() - dayStart) / 60_000;
      const to = clamp(Math.max(rawTo, from + minDur), 0, MINUTES_PER_DAY);
      return { event: e, from, to };
    })
    .filter((i) => i.to > windowStart && i.from < windowEnd)
    .sort((a, b) => a.from - b.from || b.to - a.to || a.event.id.localeCompare(b.event.id));

  const out: PositionedEvent[] = [];
  let cluster: { event: CalendarEvent; from: number; to: number; column: number }[] = [];
  let columnEnds: number[] = [];

  const flush = () => {
    const columnCount = Math.max(1, columnEnds.length);
    for (const c of cluster) {
      const from = clamp(c.from, windowStart, windowEnd);
      const to = clamp(c.to, windowStart, windowEnd);
      out.push({
        event: c.event,
        column: c.column,
        columnCount,
        top: ((from - windowStart) / span) * 100,
        height: Math.max(((to - from) / span) * 100, 0.5),
        startMinutes: from,
        endMinutes: to,
      });
    }
    cluster = [];
    columnEnds = [];
  };

  for (const item of items) {
    const clusterEnd = columnEnds.length ? Math.max(...columnEnds) : -Infinity;
    if (cluster.length && item.from >= clusterEnd) flush();

    let column = columnEnds.findIndex((end) => end <= item.from);
    if (column === -1) {
      column = columnEnds.length;
      columnEnds.push(item.to);
    } else {
      columnEnds[column] = item.to;
    }
    cluster.push({ event: item.event, from: item.from, to: item.to, column });
  }
  if (cluster.length) flush();

  return out;
}

// ─────────────────────────────── Ajanda / widget yardımcıları ───────────────────────────────

export interface AgendaDay {
  date: Date;
  key: string;
  events: CalendarEvent[];
}

/** Ajanda görünümü: etkinliği olan günler, gün gün. */
export function groupEventsByDayList(events: readonly CalendarEvent[], range: DateRange): AgendaDay[] {
  const days: Date[] = [];
  for (let d = startOfDay(range.start); d.getTime() < range.end.getTime(); d = addDays(d, 1)) days.push(d);
  const map = expandEventsByDay(eventsInRange(events, range), days);
  return days
    .map((date) => ({ date, key: dateKey(date), events: map.get(dateKey(date)) ?? [] }))
    .filter((d) => d.events.length > 0);
}

/** Yaklaşan etkinlikler (widget) — bitmemiş olanlar, başlangıca göre. */
export function upcomingEvents(events: readonly CalendarEvent[], from: Date, limit = 5): CalendarEvent[] {
  const t = from.getTime();
  return [...events]
    .filter((e) => Math.max(e.end.getTime(), e.start.getTime() + 1) > t)
    .sort((a, b) => a.start.getTime() - b.start.getTime() || a.id.localeCompare(b.id))
    .slice(0, limit);
}

// ─────────────────────────────── Etiketler ───────────────────────────────

/**
 * Bir günün "bugün / yarın / dün" olup olmadığı. Yaklaşan etkinlikler listesinde ham
 * tarih ("21 Eylül") okurken zihinsel hesap gerektiriyor; en çok bakılan iki gün ad ile
 * yazılınca liste bir bakışta anlaşılıyor. Diğer günler için null döner, çağıran tarihi
 * kendi biçiminde yazar.
 */
export function relativeDayKey(date: Date, today: Date = new Date()): 'today' | 'tomorrow' | 'yesterday' | null {
  const diff = Math.round((startOfDay(date).getTime() - startOfDay(today).getTime()) / 86_400_000);
  if (diff === 0) return 'today';
  if (diff === 1) return 'tomorrow';
  if (diff === -1) return 'yesterday';
  return null;
}

export function monthName(monthIndex: number, locale = 'tr-TR'): string {
  if (locale.startsWith('tr')) return TR_MONTHS[monthIndex] ?? '';
  return new Intl.DateTimeFormat(locale, { month: 'long' }).format(new Date(2021, monthIndex, 1));
}

/** Pazartesi başlangıçlı kısa gün adları (weekStartsOn'a göre döndürülür). */
export function weekdayLabels(
  locale = 'tr-TR',
  weekStartsOn = 1,
  style: 'short' | 'long' = 'short',
): string[] {
  const base = locale.startsWith('tr')
    ? style === 'short'
      ? TR_WEEKDAYS_SHORT
      : TR_WEEKDAYS_LONG
    : Array.from({ length: 7 }, (_, i) =>
        new Intl.DateTimeFormat(locale, { weekday: style === 'short' ? 'short' : 'long' }).format(
          new Date(2021, 2, 1 + i),
        ),
      );
  const shift = (weekStartsOn - 1 + 7) % 7;
  return Array.from({ length: 7 }, (_, i) => base[(i + shift) % 7]!);
}

export function weekdayName(d: Date, locale = 'tr-TR', style: 'short' | 'long' = 'long'): string {
  return weekdayLabels(locale, 1, style)[weekdayIndex(d, 1)]!;
}

/** "14 Nisan 2026" */
export function formatDayLabel(d: Date, locale = 'tr-TR'): string {
  return `${d.getDate()} ${monthName(d.getMonth(), locale)} ${d.getFullYear()}`;
}

/** Araç çubuğunun başlık metni. */
export function rangeLabel(
  anchor: Date,
  view: CalendarViewKind,
  locale = 'tr-TR',
  options: { agendaDays?: number } = {},
): string {
  switch (view) {
    case 'month':
      return `${monthName(anchor.getMonth(), locale)} ${anchor.getFullYear()}`;
    case 'day':
      return `${formatDayLabel(anchor, locale)}, ${weekdayName(anchor, locale)}`;
    case 'week':
    case 'agenda': {
      const range = visibleRange(anchor, view, options);
      const first = range.start;
      const last = addDays(range.end, -1);
      if (first.getFullYear() !== last.getFullYear())
        return `${formatDayLabel(first, locale)} – ${formatDayLabel(last, locale)}`;
      if (first.getMonth() !== last.getMonth())
        return `${first.getDate()} ${monthName(first.getMonth(), locale)} – ${formatDayLabel(last, locale)}`;
      return `${first.getDate()} – ${formatDayLabel(last, locale)}`;
    }
  }
}

/** "09:30" */
export function formatHourMinute(d: Date): string {
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Etkinlik zaman metni: tüm gün ya da "09:30 – 10:45". */
export function formatEventTime(e: CalendarEvent, allDayLabel = 'Tüm gün'): string {
  if (e.allDay) return allDayLabel;
  const start = formatHourMinute(e.start);
  const end = formatHourMinute(e.end);
  return start === end ? start : `${start} – ${end}`;
}
